import * as Yup from 'yup';

// ═════════════════════════════════════════════════════════════════════════
// Helper: only validate a field if it's visible via FormConfig
// ═════════════════════════════════════════════════════════════════════════
const req = (visible, schema) => (visible ? schema : Yup.mixed().notRequired());


// Step 1: Patient Info  (locked fields are always validated)
export const getStep1Schema = () => Yup.object({
  usedBefore:    Yup.string().required('Please select if you have used BHA services before'),
  firstName:     Yup.string().required('Please enter your first name'),
  lastName:      Yup.string().required('Please enter your last name'),
  dateOfBirth:   Yup.string().required('Please enter your date of birth'),
  emailAddress:  Yup.string().email('Please enter a valid email address').required('Please enter your email address'),
  selectedEvent: Yup.string().required('Please select which event you are attending'),
});

// Returning user — Step 1 + consents inline
export const getReturningUserSchema = (v = () => true) => Yup.object({
  usedBefore:    Yup.string().required('Please select if you have used BHA services before'),
  firstName:     Yup.string().required('Please enter your first name'),
  lastName:      Yup.string().required('Please enter your last name'),
  dateOfBirth:   Yup.string().required('Please enter your date of birth'),
  emailAddress:  Yup.string().email('Please enter a valid email address').required('Please enter your email address'),
  selectedEvent: Yup.string().required('Please select which event you are attending'),
  insuranceCoverage: req(v('insuranceCoverage'), Yup.string().required('Please select your insurance coverage')),
  doctorHelpInterest: req(v('doctorHelpInterest'), Yup.string().required('Please select an option')),
  mentalHealthInterest: req(v('mentalHealthInterest'), Yup.string().required('Please select an option')),
  mediaRelease:  req(v('mediaRelease'), Yup.string().required('Media release selection is required')),
  endlessHealthAcknowledgment: req(v('endlessHealthAcknowledgment'), Yup.string().required('You must acknowledge the Endless Health testing information')),
  dataSharingPermission: req(v('dataSharingPermission'), Yup.string().required('Data sharing preference is required')),
  heartGuardChampionOptIn: req(v('heartGuardChampionOptIn'), Yup.string().required('Heart Guard Champion opt-in preference is required')),
  bhaWaiverConsent: req(v('bhaWaiverConsent'), Yup.boolean().oneOf([true], 'You must accept the Terms and Conditions').required('You must accept the Terms and Conditions')),
});

// Step 2: Contact & Demographics
export const getStep2Schema = (v = () => true) => Yup.object({
  phoneNumber:      req(v('phoneNumber'), Yup.string().matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Please enter a valid phone number in format (XXX) XXX-XXXX').required('Please enter your phone number')),
  stateOfResidence: req(v('stateOfResidence'), Yup.string().required('Please select your state of residence')),
  eventZip:         req(v('eventZip'), Yup.string().matches(/^\d{5}$/, 'Please enter a valid 5-digit zip code').required('Please enter your zip code')),
  birthGender:      req(v('birthGender'), Yup.string().required('Please select your birth gender')),
  raceEthnicity:    req(v('raceEthnicity'), Yup.string().required('Please select your race/ethnicity')),
  householdIncome:  req(v('householdIncome'), Yup.string().required('Please select your household income range')),
  householdSize:    req(v('householdSize'), Yup.string().required('Please select your household size')),
  insuranceCoverage: req(v('insuranceCoverage'), Yup.string().required('Please select your insurance coverage')),
  doctorHelpInterest: req(v('doctorHelpInterest'), Yup.string().required('Please select an option')),
});

// Step 3: Medical History
export const getStep3Schema = (v = () => true) => Yup.object({
  heartAttackOrStroke:       req(v('heartAttackOrStroke'), Yup.array()),
  stressLevel:               req(v('stressLevel'), Yup.string().required('Please rate your stress level')),
  mentalHealthInterest:      req(v('mentalHealthInterest'), Yup.string().required('Please select an option')),
  medicalConditions:         req(v('medicalConditions'), Yup.array().min(1, 'Please select at least one option')),
  isParentOrCaregiver:       req(v('isParentOrCaregiver'), Yup.string().required('Please select an option')),
  currentMedications:        req(v('currentMedications'), Yup.array().min(1, 'Please select at least one option')),
  physicalActivityFrequency: req(v('physicalActivityFrequency'), Yup.string().required('Please select an option')),
  resourceAccess:            req(v('resourceAccess'), Yup.array().min(1, 'Please select at least one option')),
  referredToCardiologist:    req(v('referredToCardiologist'), Yup.string().when('medicalConditions', {
    is: (mc) => mc && mc.length > 0 && !mc.includes('none'),
    then: (schema) => schema.required('This field is required'),
    otherwise: (schema) => schema.notRequired(),
  })),
});

// Step 4: Consents & Releases
export const getStep4Schema = (v = () => true) => Yup.object({
  mediaRelease:              req(v('mediaRelease'), Yup.string().required('Media release selection is required')),
  endlessHealthAcknowledgment: req(v('endlessHealthAcknowledgment'), Yup.string().required('You must acknowledge the Endless Health testing information')),
  dataSharingPermission:     req(v('dataSharingPermission'), Yup.string().required('Data sharing preference is required')),
  heartGuardChampionOptIn:   req(v('heartGuardChampionOptIn'), Yup.string().required('Heart Guard Champion opt-in preference is required')),
  bhaWaiverConsent:          req(v('bhaWaiverConsent'), Yup.boolean().oneOf([true], 'You must accept the Terms and Conditions').required('You must accept the Terms and Conditions')),
  healthConcern:             Yup.string(),
  healthSupport:             Yup.string(),
});

// Build schema array on each call — schemas react to current FormConfig visibility
const getSchemas = (v) => [
  getStep1Schema(v),
  getStep2Schema(v),
  getStep3Schema(v),
  getStep4Schema(v),
];

// Get validation schema based on current step, user type, and field visibility
export const getValidationSchema = (currentStep, isReturningUser, isFieldVisible = () => true) => {
  if (isReturningUser && currentStep === 0) {
    return getReturningUserSchema(isFieldVisible);
  }
  return getSchemas(isFieldVisible)[currentStep];
};
